import React from 'react';
import { Button } from '@/components/ui/Button';
import { SapIntegrationLogItem } from '@/types/domain/sap-simulator';
import { Code, X } from 'lucide-react';

interface SapPayloadInspectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  logItem: SapIntegrationLogItem | null;
}

export const SapPayloadInspectorModal: React.FC<SapPayloadInspectorModalProps> = ({
  isOpen,
  onClose,
  logItem,
}) => {
  if (!isOpen || !logItem) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-md p-4 animate-in fade-in">
      <div className="w-full max-w-2xl rounded-2xl border border-border bg-surface dark:bg-[#0e101f] text-foreground p-6 shadow-2xl space-y-4 font-heading relative z-50">
        <div className="flex items-center justify-between border-b border-border pb-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary/10 text-primary">
              <Code className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-heading text-base font-extrabold text-foreground">
                Integration Payload Inspector: {logItem.apiName}
              </h3>
              <p className="text-xs text-foreground-secondary font-mono">
                Correlation Ref: {logItem.correlationId} • Duration: {logItem.durationMs}ms
              </p>
            </div>
          </div>

          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0 rounded-full">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
          {/* Request Payload JSON */}
          <div className="space-y-1.5">
            <label className="font-bold text-foreground block">Request Payload (ProcureAI → SAP)</label>
            <pre className="w-full rounded-xl border border-border bg-muted/60 p-3 font-mono text-[11px] text-foreground overflow-x-auto max-h-60 custom-scrollbar">
              {logItem.requestPayloadJson}
            </pre>
          </div>

          {/* Response Payload JSON */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between font-bold">
              <span>Response Payload (SAP → ProcureAI)</span>
              <span
                className={`text-[10px] uppercase font-extrabold px-1.5 py-0.5 rounded ${
                  logItem.status === 'SUCCESS'
                    ? 'bg-emerald-500/10 text-emerald-700'
                    : 'bg-rose-500/10 text-rose-700'
                }`}
              >
                {logItem.status}
              </span>
            </div>
            <pre className="w-full rounded-xl border border-border bg-muted/60 p-3 font-mono text-[11px] text-foreground overflow-x-auto max-h-60 custom-scrollbar">
              {logItem.responsePayloadJson}
            </pre>
          </div>
        </div>

        <div className="flex items-center justify-end pt-2 border-t border-border">
          <Button variant="outline" onClick={onClose} className="rounded-xl font-bold">
            Close Inspector
          </Button>
        </div>
      </div>
    </div>
  );
};
